import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, CheckCircle, BarChart2, Award, Users, Sparkles, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SiteCTA from '@/components/SiteCTA';

// Animated background component
const AnimatedBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
      {/* Animated grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
      {/* Floating orbs */}
      <motion.div
        className="absolute top-20 right-20 w-96 h-96 bg-gold/20 rounded-full blur-3xl"
        animate={{
          x: [0, 100, 0],
          y: [0, -50, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl"
        animate={{
          x: [0, -50, 0],
          y: [0, 100, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </div>
  );
};

const AssessmentLandingPage: React.FC = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, -150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <AnimatedBackground />
        
        {/* Hero content */}
        <motion.div 
          style={{ y, opacity }}
          className="container mx-auto px-4 relative z-10 text-center"
        >
          {/* Animated badge */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-gold/20 to-amber-400/20 rounded-full px-6 py-3 mb-8 border border-gold/30 backdrop-blur-sm"
          >
            <Sparkles className="w-5 h-5 text-gold animate-pulse" />
            <span className="text-gold font-bold text-sm uppercase tracking-wider">Free PMO Assessment</span>
            <Sparkles className="w-5 h-5 text-gold animate-pulse" />
          </motion.div>

          {/* Main heading with staggered animation */}
          <motion.div className="mb-8">
            <motion.h1 
              className="text-5xl md:text-7xl lg:text-8xl font-bold leading-tight"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold animate-gradient">
                Assess Your PMO
              </span>
              <motion.span 
                className="block text-white mt-4"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Maturity
              </motion.span>
            </motion.h1>
          </motion.div>

          {/* Subtitle */}
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Discover where your PMO stands and unlock actionable insights to elevate your organization's project delivery capabilities.
          </motion.p>

          {/* CTA Button */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                asChild 
                size="lg" 
                className="bg-gradient-to-r from-gold to-amber-400 hover:from-amber-400 hover:to-gold text-black px-12 py-8 text-xl rounded-full shadow-2xl hover:shadow-gold/50 transition-all font-bold group"
              >
                <Link to="/assessment/contact" className="flex items-center gap-3">
                  Start Your Free Assessment
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 inset-x-0 flex justify-center"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col items-center gap-2 text-gold">
            <span className="text-sm uppercase tracking-wider">Scroll to explore</span>
            <ChevronDown className="w-6 h-6" />
          </div>
        </motion.div>
      </section>
      
      {/* Mobile preview - only shown on small screens */}
      <div className="block md:hidden px-4 py-6">
        <motion.div 
          className="w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7 }}
        >
          <div className="bg-white rounded-xl shadow-xl p-4 border border-gray-200">
            <div className="flex items-center mb-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className="bg-primary h-2.5 rounded-full w-[45%]"></div>
              </div>
              <span className="ml-4 text-sm font-medium text-gray-500">5/12</span>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg mb-2">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm">How effectively does your PMO align projects with strategic business objectives?</h3>
              <div className="space-y-2 mt-2">
                <div className="flex items-center">
                  <input type="radio" id="m-option1" name="m-rating" className="h-4 w-4 text-deepBlue" disabled />
                  <label htmlFor="m-option1" className="ml-2 text-xs text-gray-700">Projects rarely align with strategic objectives</label>
                </div>
                <div className="flex items-center">
                  <input type="radio" id="m-option3" name="m-rating" className="h-4 w-4 text-deepBlue" checked disabled />
                  <label htmlFor="m-option3" className="ml-2 text-xs text-gray-700">Most projects align with strategic objectives</label>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          {/* Section Title with animation */}
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <motion.h2 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                Why Take Our PMO Maturity Assessment?
              </span>
            </motion.h2>
            <motion.p 
              className="text-lg text-gray-300"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Our assessment delivers valuable insights into your PMO's current state—highlighting strengths, revealing gaps, and helping you better understand where your organization stands in its PMO journey.
            </motion.p>
          </motion.div>

          {/* Split layout with image on left and cards on right */}
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Left side - Assessment Results Image with effects */}
            <motion.div 
              className="w-full lg:w-1/2 relative"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                {/* Subtle glow effect behind image */}
                <div className="absolute -inset-4 bg-gradient-to-r from-gold/10 to-deepBlue/10 rounded-2xl blur-lg opacity-50"></div>
                
                {/* Main image with minimal glass effect */}
                <div className="relative overflow-hidden rounded-xl shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-r from-deepBlue/5 to-gold/5 backdrop-blur-[2px] z-10"></div>
                  <img 
                    src="/results-sample.png" 
                    alt="PMO Assessment Results Dashboard" 
                    className="w-full h-auto relative z-0 transform hover:scale-105 transition-transform duration-700"
                  />
                  
                  {/* Tags removed as requested */}
                </div>
                
                {/* Decorative elements - more subtle */}
                <div className="absolute -bottom-4 -right-4 h-20 w-20 bg-gradient-to-r from-gold to-gold-light rounded-full blur-lg opacity-30"></div>
                <div className="absolute -top-4 -left-4 h-14 w-14 bg-gradient-to-r from-deepBlue to-blue-400 rounded-full blur-lg opacity-30"></div>
              </div>
            </motion.div>
            
            {/* Right side - Stacked Benefits Cards */}
            <div className="w-full lg:w-1/2 flex items-center">
              <div className="flex flex-col justify-between h-full space-y-6 py-2 w-full">
                {/* Actionable Insights Card */}
                <motion.div 
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  whileHover={{ x: 5, boxShadow: "0 4px 12px -2px rgba(0, 0, 0, 0.08)" }}
                  className="bg-white rounded-lg shadow-md border-l-4 border-l-deepBlue border-t border-r border-b border-gray-100 flex items-center"
                >
                  <div className="p-5 flex items-center w-full">
                    <motion.div 
                      className="h-12 w-12 bg-deepBlue/10 rounded-lg flex items-center justify-center mr-5 flex-shrink-0"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                    >
                      <Award className="h-6 w-6 text-deepBlue" />
                    </motion.div>
                    <div>
                      <motion.h3 
                        className="text-lg font-semibold text-deepBlue mb-2"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                      >
                        Actionable Insights
                      </motion.h3>
                      <motion.ul
                        className="space-y-1 mb-3"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                      >
                        <li className="flex items-center text-sm text-charcoal-light">
                          <CheckCircle className="h-3 w-3 text-deepBlue mr-2 flex-shrink-0" />
                          <span>See where your PMO excels and where it can improve</span>
                        </li>
                      </motion.ul>
                      <motion.ul
                        className="space-y-1"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        <li className="flex items-center text-sm text-charcoal-light">
                          <CheckCircle className="h-3 w-3 text-deepBlue mr-2 flex-shrink-0" />
                          <span>Receive practical, time-based recommendations to guide next steps</span>
                        </li>
                      </motion.ul>
                    </div>
                  </div>
                </motion.div>

                {/* Expert Guidance Card */}
                <motion.div 
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  whileHover={{ x: 5, boxShadow: "0 4px 12px -2px rgba(0, 0, 0, 0.08)" }}
                  className="bg-white rounded-lg shadow-md border-l-4 border-l-deepBlue border-t border-r border-b border-gray-100 flex items-center"
                >
                  <div className="p-5 flex items-center w-full">
                    <motion.div 
                      className="h-12 w-12 bg-deepBlue/10 rounded-lg flex items-center justify-center mr-5 flex-shrink-0"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                    >
                      <Users className="h-6 w-6 text-deepBlue" />
                    </motion.div>
                    <div>
                      <motion.h3 
                        className="text-lg font-semibold text-deepBlue mb-2"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                      >
                        Expert Perspective
                      </motion.h3>
                      <motion.ul
                        className="space-y-1 mb-3"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        <li className="flex items-center text-sm text-charcoal-light">
                          <CheckCircle className="h-3 w-3 text-deepBlue mr-2 flex-shrink-0" />
                          <span>Access maturity insights grounded in real-world PMO practices</span>
                        </li>
                      </motion.ul>
                      <motion.ul
                        className="space-y-1"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.5 }}
                      >
                        <li className="flex items-center text-sm text-charcoal-light">
                          <CheckCircle className="h-3 w-3 text-deepBlue mr-2 flex-shrink-0" />
                          <span>Understand what's needed to progress toward higher performance</span>
                        </li>
                      </motion.ul>
                    </div>
                  </div>
                </motion.div>

                {/* Success Roadmap Card */}
                <motion.div 
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  whileHover={{ x: 5, boxShadow: "0 4px 12px -2px rgba(0, 0, 0, 0.08)" }}
                  className="bg-white rounded-lg shadow-md border-l-4 border-l-deepBlue border-t border-r border-b border-gray-100 flex items-center"
                >
                  <div className="p-5 flex items-center w-full">
                    <motion.div 
                      className="h-12 w-12 bg-deepBlue/10 rounded-lg flex items-center justify-center mr-5 flex-shrink-0"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.4 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-deepBlue" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                      </svg>
                    </motion.div>
                    <div>
                      <motion.h3 
                        className="text-lg font-semibold text-deepBlue mb-2"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        Maturity Snapshot
                      </motion.h3>
                      <motion.ul
                        className="space-y-1 mb-3"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.5 }}
                      >
                        <li className="flex items-center text-sm text-charcoal-light">
                          <CheckCircle className="h-3 w-3 text-deepBlue mr-2 flex-shrink-0" />
                          <span>View your current PMO stage and percentile ranking</span>
                        </li>
                      </motion.ul>
                      <motion.ul
                        className="space-y-1"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.6 }}
                      >
                        <li className="flex items-center text-sm text-charcoal-light">
                          <CheckCircle className="h-3 w-3 text-deepBlue mr-2 flex-shrink-0" />
                          <span>Get a breakdown of strengths across 8 key PMO focus areas</span>
                        </li>
                      </motion.ul>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>



      <SiteCTA />
    </div>
  );
};

export default AssessmentLandingPage;
